import { Button, Textarea, TextInput } from '@any-ui-react/core';
import { vestResolver } from '@hookform/resolvers/vest';
import type { CountryCode } from 'libphonenumber-js/min';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ZodError } from 'zod';

import { PhoneNumberInput } from '@/common/components/employees/phone-number-input/PhoneNumberInput';
import { ModalFooter } from '@/common/components/shared/modal-footer/ModalFooter';
import { EmergencyContactRelationSelectSingle } from '@/common/components/shared/select/emergency-contact-relation/EmergencyContactRelationSelect';
import { toast } from '@/common/components/shared/toaster/toast';
import { useEmployeeDetailContext } from '@/common/contexts/EmployeeDetailContext';
import { EmergencyContactRelation } from '@/common/graphql';
import { type EmergencyContactFormDataType, type EmergencyContactType, EmployeeEmergencyContact } from '@/common/types';

import { emergencyContactFormValidationSuite } from './emergency-contact-form.validator';

export interface EmergencyContactFormProps {
  onSuccess: (data: EmergencyContactType) => void;
  onCancel: () => void;
  emergencyContact?: EmergencyContactType;
}

export const EmergencyContactForm = ({ onSuccess, onCancel, emergencyContact }: EmergencyContactFormProps) => {
  const { employeeDetail } = useEmployeeDetailContext();
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    setValue,
    clearErrors,
    watch,
  } = useForm<EmergencyContactFormDataType>({
    defaultValues: { ...emergencyContact },
    resolver: vestResolver(emergencyContactFormValidationSuite),
  });

  const onSubmit = useMemo(
    () =>
      handleSubmit(async formValues => {
        try {
          onSuccess(EmployeeEmergencyContact.parse({ ...formValues, id: emergencyContact?.id }));
        } catch (e) {
          if (e instanceof ZodError) {
            const message = e.errors.map(it => `${it.path.join('.')}: ${it.message}`).join('\n');
            toast.error(message);
          }
        }
      }),
    [emergencyContact?.id, handleSubmit, onSuccess],
  );

  return (
    <form noValidate={true}>
      <fieldset className="form-fieldset w-full">
        <TextInput
          {...register('name')}
          autoComplete="off"
          autoCorrect="off"
          className="col-span-2"
          data-1p-ignore={true}
          error={errors.name?.message}
          label="Name"
          placeholder="Enter name"
          required={true}
        />
        <Controller
          control={control}
          name="relation"
          render={({ field: { value } }) => (
            <div className="col-span-2">
              <EmergencyContactRelationSelectSingle
                error={errors.relation?.message}
                label="Relation"
                overrideLabel={value => {
                  switch (value) {
                    case EmergencyContactRelation.Other:
                      return 'Others (Input details in Remarks)';
                    default:
                      return false;
                  }
                }}
                required={true}
                value={value}
                onChange={newValue => {
                  setValue('relation', newValue?.value ?? null, { shouldDirty: true });
                  clearErrors('relation');
                }}
              />
            </div>
          )}
        />
        <PhoneNumberInput
          {...register('phoneNumber')}
          className="col-span-2"
          preferredCountryCode={(employeeDetail?.generalInformation?.companyCountryId as CountryCode) || undefined}
          defaultValue={{
            countryCode: (emergencyContact?.phoneNumberCountryCode as CountryCode) || '',
            number: emergencyContact?.phoneNumber || '',
          }}
          error={errors.phoneNumberCountryCode?.message || errors.phoneNumber?.message}
          label="Phone Number"
          required={true}
          onChange={({ number, countryCode }) => {
            setValue('phoneNumber', number);
            if (countryCode) {
              setValue('phoneNumberCountryCode', countryCode);
            }
          }}
        />
        <Textarea
          {...register('remarks')}
          autosize={true}
          className="col-span-2"
          error={errors.remarks?.message}
          label="Remarks"
          minRows={5}
          placeholder="Enter remarks"
          required={watch('relation') === EmergencyContactRelation.Other}
        />
      </fieldset>

      <ModalFooter spacerSize={-1}>
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="bg-blue-6" loading={isSubmitting} type="submit" onClick={onSubmit}>
          Save
        </Button>
      </ModalFooter>
    </form>
  );
};
